import { useCallback, useState } from "react";

import clsx from "clsx";
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { Steps } from "@pages/onboarding/steps";
import { PrimaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

import { StepCompletionConfirmation as WrappedStepCompletionConfirmation } from "../step-completion-confirmation";

import PlatformIcon from "./platforms/components/platform-icon";
import { type Platform } from "./platforms/constants";
import {
  sortPlatformMetadata,
  usePlatformsMetadata,
} from "./platforms/metadata";
import { type PlatformState } from "./platforms/platform-context";

import { type StepMetadata } from ".";

export const MissingStepsModal = ({
  isOpen,
  onRequestClose,
  remainingPlatforms,
  footer,
}: {
  isOpen: boolean;
  onRequestClose: () => void;
  remainingPlatforms: { [k in Platform]?: PlatformState };
  footer: React.ReactNode;
}) => {
  const [_, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    noScroll: withDefault(BooleanParam, false),
  });

  const {
    isConnectionSupported,
    isUTMSupported,
    getTitleForPlatform,
    getMetadata,
  } = usePlatformsMetadata();

  const navToPlatform = useCallback(
    (platform: Platform) => {
      return (e: any) => {
        onRequestClose();
        e.preventDefault();
        setSearchParams({
          subStep: `c_${platform}`,
          noScroll: undefined,
        });
      };
    },
    [remainingPlatforms],
  );

  return (
    <Modal
      titleContent={
        <div className="font-bold">
          Finish setting up the following platforms to mark this step ‘complete’
        </div>
      }
      isOpen={isOpen}
      onClose={onRequestClose}
    >
      <div className="h-[280px] flex flex-col gap-4">
        <div className="self-stretch h-px bg-[#D9D9D9]" />
        <div className="w-full flex flex-col items-start overflow-y-scroll">
          {Object.entries(remainingPlatforms)
            .sort(([lhs], [rhs]) => {
              return sortPlatformMetadata(
                getMetadata(lhs as Platform),
                getMetadata(rhs as Platform),
              );
            })
            .map(([platform, state]) => (
              <div key={platform} className="w-full">
                <div className="self-stretch h-11 flex-col justify-start items-start flex my-4">
                  <div className="self-stretch justify-between items-center inline-flex h-11">
                    <div className="flex justify-start items-center gap-2">
                      <PlatformIcon platform={platform as Platform} />
                      <div className="font-semibold leading-loose">
                        {getTitleForPlatform(platform as Platform)}
                      </div>
                    </div>
                    <div className="items-center inline-flex">
                      {isConnectionSupported(platform as Platform) && (
                        <StatusIcon
                          isConfigured={state.isConnectionConfigured}
                          label="integration"
                          configureAction={
                            <a
                              href="#"
                              onClick={navToPlatform(platform as Platform)}
                              className="text-xs font-semibold leading-none"
                            >
                              Configure
                            </a>
                          }
                        />
                      )}
                      <div
                        className={clsx(
                          "mx-4 w-px h-6",
                          isConnectionSupported(platform as Platform) &&
                            isUTMSupported(platform as Platform) &&
                            "bg-gray-0",
                        )}
                      />
                      <StatusIcon
                        isConfigured={state.isUTMsConfigured}
                        label="utm"
                        configureAction={
                          <a
                            href="#"
                            onClick={navToPlatform(platform as Platform)}
                            className="text-xs font-semibold leading-none"
                          >
                            View instructions
                          </a>
                        }
                        spacingOnly={!isUTMSupported(platform as Platform)}
                      />
                    </div>
                  </div>
                </div>
                <div className="self-stretch h-px bg-[#D9D9D9]" />
              </div>
            ))}
        </div>
      </div>
      <div className="w-full h-10 self-stretch justify-end items-end inline-flex">
        {footer}
      </div>
    </Modal>
  );
};

const StatusIcon = ({
  isConfigured,
  label,
  configureAction,
  spacingOnly = false,
  className,
}: {
  isConfigured: boolean;
  label: "integration" | "utm" | string;
  configureAction: React.ReactNode;
  spacingOnly?: boolean;
  className?: string;
}) => (
  <div
    className={clsx(
      "h-11 pl-2 pr-4 py-1 rounded-3xl justify-start items-center gap-2 inline-flex",
      {
        "bg-alien-2": isConfigured,
        "bg-interstellar-3": !isConfigured,
        "opacity-0": spacingOnly,
      },
      className,
    )}
  >
    <div className="w-6 h-6 relative inline-flex justify-center items-center">
      <div
        className={clsx(
          "w-6 h-6 rounded-full inline-flex justify-center items-center",
          {
            "bg-alien": isConfigured,
            "bg-gray": !isConfigured,
          },
        )}
      >
        <i
          className={clsx("fa-solid text-center text-white text-xxs", {
            "fa-check": isConfigured,
            "fa-times": !isConfigured,
          })}
        />
      </div>
    </div>
    <div className="h-7 flex-col justify-between items-start inline-flex">
      <div className="text-xs font-semibold uppercase leading-tight">
        {label}
      </div>
      <div className="justify-start items-center gap-1 inline-flex">
        {isConfigured ? (
          <div className="text-alien text-xs font-semibold leading-none">
            Connected
          </div>
        ) : (
          configureAction
        )}
      </div>
    </div>
  </div>
);

interface StepCompletionConfirmationProps {
  stepMetadata: StepMetadata;
  nextStep?: () => void;
  toggleStepComplete: () => void;
}

export const StepCompletionConfirmation = ({
  stepMetadata,
  nextStep,
  toggleStepComplete,
}: StepCompletionConfirmationProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { platformsMissingConfiguration } = usePlatformsMetadata();

  const remainingPlatforms = platformsMissingConfiguration(stepMetadata);
  const remainingPlatformsLength = Object.keys(remainingPlatforms).length;
  return (
    <>
      <MissingStepsModal
        isOpen={isOpen}
        onRequestClose={() => {
          setIsOpen(false);
        }}
        remainingPlatforms={remainingPlatforms}
        footer={
          <PrimaryButton
            className="w-[120px] h-full font-semibold uppercase rounded-[20px]"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            OK
          </PrimaryButton>
        }
      />
      <WrappedStepCompletionConfirmation
        labelSuffix="connecting all platform integrations and UTMs"
        step={Steps.CONFIGURE_PLATFORMS}
        nextStep={nextStep}
        onSelect={() => {
          setIsOpen(remainingPlatformsLength > 0);

          return !remainingPlatformsLength;
        }}
        toggleStepComplete={toggleStepComplete}
      />
    </>
  );
};
